import { apiInstance } from "../base"
import type { ISearchFlight } from "./types"

export interface SavedFlightSegment {
    dep_airport_code: string
    arr_airport_code: string
    dep_date_time: string
    arr_date_time: string
    flight_number: string
    marketing_airline: string
    op_airline?: string
    aircraft_type?: string
}

export interface SavedFlightSnapshot {
    dep_airport_code: string
    arr_airport_code: string
    total_duration: number
    segments: SavedFlightSegment[]
    price: { amount: number; currency: string; validating_company?: string }
}

export interface SavedFlightItem {
    id: string
    offer_id: string
    snapshot: SavedFlightSnapshot
    saved_at: string
    historical: true
}

export interface SavedFlightsPage {
    items: SavedFlightItem[]
    total: number
    limit: number
    offset: number
}

export const listSavedFlights = (offset = 0) =>
    apiInstance.handler.get<SavedFlightsPage>("/aviabooking/saved-flights", { params: { limit: 20, offset } })

export const saveFlight = (offer_id: string, snapshot: SavedFlightSnapshot) =>
    apiInstance.handler.post<SavedFlightItem>("/aviabooking/saved-flights", { offer_id, snapshot })

export const deleteSavedFlight = (id: string) =>
    apiInstance.handler.delete<void>(`/aviabooking/saved-flights/${encodeURIComponent(id)}`)

export interface SavedFlightMatch {
    id: string
    offer_id: string
}

export const lookupSavedFlights = (offer_ids: string[]) =>
    apiInstance.handler.post<SavedFlightMatch[]>("/aviabooking/saved-flights/lookup", { offer_ids })

const airportCode = (value: string) => /^[A-Z]{3}$/.test(value)
const airlineCode = (value: string) => /^[A-Za-z0-9]{1,8}$/.test(value)
const timestamp = (value: string) => typeof value === "string" && value.length <= 35 &&
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/.test(value) &&
    !Number.isNaN(Date.parse(value))

/** Persist only the backend's historical projection, never the search offer or passenger data. */
export function snapshotFromFlight(flight: ISearchFlight): SavedFlightSnapshot | null {
    if (!airportCode(flight.dep_airport_code) || !airportCode(flight.arr_airport_code) ||
        !Number.isInteger(flight.total_duration) || flight.total_duration < 0 || flight.total_duration > 100000 ||
        !Array.isArray(flight.segments) || flight.segments.length < 1 || flight.segments.length > 12) return null
    const segments: SavedFlightSegment[] = []
    for (const segment of flight.segments) {
        if (!airportCode(segment.dep_airport_code) || !airportCode(segment.arr_airport_code) ||
            !timestamp(segment.dep_date_time) || !timestamp(segment.arr_date_time) ||
            !/^[A-Za-z0-9 /-]{1,16}$/.test(segment.flight_number) || !airlineCode(segment.marketing_airline)) return null
        segments.push({
            dep_airport_code: segment.dep_airport_code,
            arr_airport_code: segment.arr_airport_code,
            dep_date_time: segment.dep_date_time,
            arr_date_time: segment.arr_date_time,
            flight_number: segment.flight_number,
            marketing_airline: segment.marketing_airline,
            ...(airlineCode(segment.op_airline) && { op_airline: segment.op_airline }),
            ...(typeof segment.aircraft_type === "string" && segment.aircraft_type.length >= 1 &&
                segment.aircraft_type.length <= 32 && { aircraft_type: segment.aircraft_type }),
        })
    }
    const price = flight.prices?.[0]
    if (!price || !Array.isArray(price.fares) || !price.fares.length) return null
    let amount = 0
    let currency: string | undefined
    for (const fare of price.fares) {
        if (!Number.isInteger(fare.quantity) || fare.quantity < 0 ||
            !Number.isFinite(fare.total_fare) || fare.total_fare < 0 ||
            !/^[A-Z]{3}$/.test(fare.currency) || (currency && currency !== fare.currency)) return null
        currency = fare.currency
        amount += fare.quantity * fare.total_fare
    }
    amount = Math.round((amount + Number.EPSILON) * 100) / 100
    if (!Number.isFinite(amount) || amount > 1000000000 || !currency) return null
    return {
        dep_airport_code: flight.dep_airport_code,
        arr_airport_code: flight.arr_airport_code,
        total_duration: flight.total_duration,
        segments,
        price: {
            amount,
            currency,
            ...(airlineCode(price.validating_company) && { validating_company: price.validating_company }),
        },
    }
}
